import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} Flavour
 * @property {string} key
 * @property {string} label
 * @property {string} emoji
 * @property {number} variantId
 * @property {string} image
 * @property {number} tinVariantId
 * @property {number|string} tinPlanId
 * @property {number} tinPlanCents
 * @property {number} tinOneTimeCents
 */

/**
 * @typedef {object} Config
 * @property {string} moneyFormat
 * @property {string} [currency]
 * @property {number} cafeServeCents
 * @property {number} servings
 * @property {{productId: number, variantId: number, priceCents: number, compareCents: number, planId: number|string, planCents: number, image: string, imageAlt: string, title: string}} kit
 * @property {{whiskCents: number, tumblerCents: number, spoonCents: number, whiskImage: string, tumblerImage: string, spoonImage: string}} [accessories]
 * @property {Flavour[]} flavours
 * @property {{alt: string, src: string, thumb: string}[]} [media]
 */

/**
 * @typedef {object} Refs
 * @property {HTMLScriptElement} config
 * @property {HTMLImageElement} [heroImage]
 * @property {HTMLElement} [heroCopy]
 * @property {HTMLElement} flavours
 * @property {HTMLElement} purchase
 * @property {HTMLButtonElement} buyButton
 * @property {HTMLElement} [stickyBar]
 * @property {HTMLButtonElement} [stickyBuy]
 * @property {HTMLElement} [thumbs]
 * @property {HTMLInputElement} qtyInput
 * @property {HTMLElement} comparePrice
 * @property {HTMLElement} todayPrice
 * @property {HTMLElement} saveLine
 * @property {HTMLElement} cafeLine
 * @property {HTMLElement} cafeLeft
 * @property {HTMLElement} cafeRight
 * @property {HTMLElement} cafeRightCopy
 * @property {HTMLElement} cafeKeep
 * @property {HTMLButtonElement} cafeCta
 * @property {HTMLElement} [itemPowderPrice]
 * @property {HTMLElement} [itemWhiskPrice]
 * @property {HTMLElement} [itemTumblerPrice]
 * @property {HTMLElement} [itemSpoonPrice]
 * @property {HTMLElement} [heroToday]
 * @property {HTMLElement} [heroCompare]
 * @property {HTMLElement} [includedTitle]
 * @property {HTMLImageElement} [cardPowderImage]
 */

/**
 * @extends {Component<Refs>}
 */
class StarterKitOfferComponent extends Component {
  requiredRefs = ['config', 'flavours', 'purchase', 'buyButton', 'qtyInput'];

  /** @type {Config | null} */
  #config = null;

  /** @type {string} */
  #flavourKey = '';

  /** @type {boolean} */
  #subscribe = true;

  /** @type {number} */
  #qty = 1;

  /** @type {boolean} */
  #pending = false;

  /** @type {string} */
  #gallerySrc = '';

  /** @type {string} */
  #galleryAlt = '';

  /** @type {IntersectionObserver | undefined} */
  #buyObserver;

  /** @type {(() => void) | undefined} */
  #buyQueryCleanup;

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#readConfig();
    if (!this.#config) return;
    this.#flavourKey = this.#config.flavours[0]?.key || '';
    this.#renderThumbs();
    this.render();
    this.#watchBuyButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyObserver?.disconnect();
    this.#buyQueryCleanup?.();
  }

  /**
   * @param {MouseEvent} event
   */
  handleFlavourTap = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const key = button.dataset.key;
    if (!key) return;
    this.#flavourKey = key;
    this.render();
  };

  /**
   * @param {MouseEvent} event
   */
  handlePurchaseType = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    this.#subscribe = button.dataset.purchase === 'subscribe';
    this.render();
  };

  increaseQty = () => {
    this.#qty += 1;
    this.render();
  };

  decreaseQty = () => {
    this.#qty = Math.max(1, this.#qty - 1);
    this.render();
  };

  changeQty = () => {
    const value = Number(this.refs.qtyInput?.value || 1);
    this.#qty = Number.isFinite(value) ? Math.max(1, Math.round(value)) : 1;
    this.render();
  };

  /**
   * @param {MouseEvent} event
   */
  handleAddToCart = async (event) => {
    event.preventDefault();
    await this.#addToCart();
  };

  /**
   * @param {MouseEvent} event
   */
  handleBuyNow = async (event) => {
    event.preventDefault();
    await this.#addToCart();
  };

  /**
   * @param {MouseEvent} event
   */
  showMedia = (event) => {
    const button = event.target instanceof Element ? event.target.closest('.kit-offer__thumb') : null;
    if (!(button instanceof HTMLElement)) return;
    const src = button.dataset.src;
    if (!src) return;
    this.#gallerySrc = button.dataset.featured === 'true' ? '' : src;
    this.#galleryAlt = button.dataset.alt || '';
    this.#setActiveThumb(button);
    this.#updateHero();
  };

  render() {
    this.#renderFlavours();
    this.#renderPurchase();
    this.#updatePrices();
    this.#updateHero();
  }

  /**
   * @returns {Config | null}
   */
  #readConfig() {
    try {
      return JSON.parse(this.refs.config.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  #currentFlavour() {
    return this.#config?.flavours.find((flavour) => flavour.key === this.#flavourKey) || this.#config?.flavours[0];
  }

  #renderFlavours() {
    const { flavours } = this.refs;
    if (!flavours || !this.#config) return;
    flavours.replaceChildren();

    for (const flavour of this.#config.flavours) {
      const selected = flavour.key === this.#flavourKey;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `kit-offer__flavour${selected ? ' kit-offer__flavour--selected' : ''}`;
      button.dataset.key = flavour.key;
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('on:click', '/handleFlavourTap');
      button.innerHTML = `
        <span aria-hidden="true">${this.#escape(flavour.emoji || '')}</span>
        <span class="kit-offer__flavour-label">${this.#escape(flavour.label)}</span>
      `;
      flavours.append(button);
    }
  }

  #renderPurchase() {
    const { purchase } = this.refs;
    if (!purchase || !this.#config) return;
    const flavour = this.#currentFlavour();
    const kit = this.#config.kit;
    const refill = flavour?.tinPlanCents || kit.planCents;

    purchase.replaceChildren();

    const subscribe = document.createElement('button');
    subscribe.type = 'button';
    subscribe.className = `kit-offer__choice kit-offer__choice--subscribe${this.#subscribe ? ' kit-offer__choice--selected' : ''}`;
    subscribe.dataset.purchase = 'subscribe';
    subscribe.setAttribute('aria-pressed', this.#subscribe ? 'true' : 'false');
    subscribe.setAttribute('on:click', '/handlePurchaseType');
    const refillPercent = this.#discountPercent(flavour?.tinOneTimeCents || 0, refill);
    subscribe.innerHTML = `
      <span class="kit-offer__choice-title">${this.#escape(this.#t('subscribe_title'))}</span>
      <span class="kit-offer__checkout">${this.#escape(this.#t('at_checkout'))}</span>
      <span>${this.#escape(this.#t('subscribe_copy', {
        today: this.#formatMoney(kit.priceCents),
        refill: this.#formatMoney(refill),
      }))}</span>
      <ul class="kit-offer__perks">
        <li>${this.#escape(refillPercent > 0 ? this.#t('perk_refills_percent', { percent: refillPercent }) : this.#t('perk_refills'))}</li>
        <li>${this.#escape(this.#t('perk_ship'))}</li>
        <li>${this.#escape(this.#t('perk_skip'))}</li>
        <li>${this.#escape(this.#t('perk_guarantee'))}</li>
      </ul>
    `;

    const onetime = document.createElement('button');
    onetime.type = 'button';
    onetime.className = `kit-offer__choice${this.#subscribe ? '' : ' kit-offer__choice--selected'}`;
    onetime.dataset.purchase = 'onetime';
    onetime.setAttribute('aria-pressed', this.#subscribe ? 'false' : 'true');
    onetime.setAttribute('on:click', '/handlePurchaseType');
    onetime.innerHTML = `
      <span class="kit-offer__choice-title">${this.#escape(this.#t('onetime_title'))}</span>
      <span>${this.#escape(this.#t('onetime_copy', { price: this.#formatMoney(kit.priceCents) }))}</span>
    `;

    purchase.append(subscribe, onetime);
  }

  #updatePrices() {
    const kit = this.#config?.kit;
    if (!kit) return;
    const qty = this.#qty;
    const today = kit.priceCents * qty;
    const items = this.#itemCents();
    const compareEach = Math.max(items.total, kit.compareCents, kit.priceCents);
    const compare = compareEach * qty;
    const servings = (this.#config?.servings || 60) * qty;
    const cafe = (this.#config?.cafeServeCents || 700) * servings;
    const cafeSave = Math.max(cafe - today, 0);
    const per = servings > 0 ? Math.round(today / servings) : 0;

    this.#setText('itemPowderPrice', items.powder > 0 ? this.#formatMoney(items.powder) : '');
    this.#setText('itemWhiskPrice', items.whisk > 0 ? this.#formatMoney(items.whisk) : '');
    this.#setText('itemTumblerPrice', items.tumbler > 0 ? this.#formatMoney(items.tumbler) : '');
    this.#setText('itemSpoonPrice', items.spoon > 0 ? this.#formatMoney(items.spoon) : '');
    this.#setText('comparePrice', this.#formatMoney(compare));
    this.#setText('todayPrice', this.#formatMoney(today));
    this.#setText('heroToday', this.#formatMoney(today));
    this.#setText('heroCompare', compare > today ? this.#formatMoney(compare) : '');
    this.#setText('cafeLine', this.#t('cafe_line', { amount: this.#formatMoney(cafeSave) }));
    this.#setText('cafeLeft', this.#formatMoney(cafe));
    this.#setText('cafeRight', this.#formatMoney(today));
    this.#setText('cafeRightCopy', this.#t('cafe_right_copy', { per: this.#formatMoney(per, true) }));
    this.#setText('cafeKeep', this.#t('cafe_keep', { amount: this.#formatMoney(cafeSave) }));
    this.#setText('includedTitle', this.#t('included_title', { price: this.#formatMoney(kit.priceCents) }));

    if (this.refs.qtyInput) this.refs.qtyInput.value = String(qty);
    const buyLabel = this.#t('buy_now', { price: this.#formatMoney(today) });
    if (this.refs.buyButton) this.refs.buyButton.textContent = buyLabel;
    if (this.refs.stickyBuy) this.refs.stickyBuy.textContent = buyLabel;
    if (this.refs.cafeCta) {
      this.refs.cafeCta.textContent = this.#t('cafe_cta', { price: this.#formatMoney(today) });
    }
  }

  #itemCents() {
    const accessories = this.#config?.accessories || {};
    const flavour = this.#currentFlavour();
    const kit = this.#config?.kit;
    const powder = flavour?.tinOneTimeCents || 0;
    const whisk = accessories.whiskCents || 0;
    const tumbler = accessories.tumblerCents || 0;
    let spoon = accessories.spoonCents || 0;
    const known = powder + whisk + tumbler + spoon;
    const compare = kit?.compareCents || 0;

    if (spoon < 1 && compare > known) {
      spoon = compare - powder - whisk - tumbler;
    }

    return {
      powder,
      whisk,
      tumbler,
      spoon,
      total: powder + whisk + tumbler + spoon,
    };
  }

  #updateHero() {
    const image = this.refs.heroImage;
    const kit = this.#config?.kit;
    const featured = !this.#gallerySrc;
    const src = this.#gallerySrc || kit?.image;
    if (image && src) {
      image.hidden = false;
      image.src = src;
      image.alt = featured ? kit?.imageAlt || kit?.title || '' : this.#galleryAlt || kit?.title || '';
    }
    if (this.refs.heroCopy) this.refs.heroCopy.hidden = featured;

    const flavour = this.#currentFlavour();
    const cardImage = this.refs.cardPowderImage;
    const cardSrc = flavour?.image || this.#config?.kit.image;
    if (cardImage && cardSrc) {
      cardImage.hidden = false;
      cardImage.src = cardSrc;
      cardImage.alt = '';
    }
  }

  #renderThumbs() {
    const thumbs = this.refs.thumbs;
    if (!thumbs || !this.#config) return;
    const media = this.#config.media || [];
    thumbs.replaceChildren();
    thumbs.hidden = media.length < 2;

    for (const [index, item] of media.entries()) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = index === 0 ? 'kit-offer__thumb kit-offer__thumb--active' : 'kit-offer__thumb';
      button.dataset.src = item.src;
      button.dataset.alt = item.alt || '';
      if (index === 0) button.dataset.featured = 'true';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('aria-label', item.alt || this.#config?.kit.title || '');
      button.setAttribute('on:click', '/showMedia');
      const image = document.createElement('img');
      image.className = 'kit-offer__thumb-image';
      image.src = item.thumb;
      image.alt = '';
      image.width = 96;
      image.height = 96;
      button.append(image);
      thumbs.append(button);
    }
  }

  /**
   * @param {HTMLElement} active
   */
  #setActiveThumb(active) {
    const thumbs = active.parentElement?.querySelectorAll('.kit-offer__thumb');
    if (!thumbs) return;
    for (const thumb of thumbs) {
      const selected = thumb === active;
      thumb.classList.toggle('kit-offer__thumb--active', selected);
      thumb.setAttribute('aria-selected', selected ? 'true' : 'false');
    }
  }

  #watchBuyButton() {
    const button = this.refs.buyButton;
    const bar = this.refs.stickyBar;
    if (!button || !bar || typeof IntersectionObserver === 'undefined') return;

    const query = window.matchMedia('(max-width: 749px)');
    const sync = (entry) => {
      if (!query.matches || !entry) {
        bar.hidden = true;
        return;
      }
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.hidden = !scrolledPast;
    };

    this.#buyObserver = new IntersectionObserver((entries) => {
      sync(entries[0]);
    });
    this.#buyObserver.observe(button);

    const onChange = () => {
      if (!query.matches) bar.hidden = true;
    };
    query.addEventListener('change', onChange);
    this.#buyQueryCleanup = () => query.removeEventListener('change', onChange);
  }

  async #addToCart() {
    const { buyButton, cafeCta, stickyBuy } = this.refs;
    if (this.#pending) return;
    const flavour = this.#currentFlavour();
    const kit = this.#config?.kit;
    if (!flavour || !kit) return;

    this.#pending = true;
    if (buyButton) buyButton.disabled = true;
    if (stickyBuy) stickyBuy.disabled = true;
    if (cafeCta) cafeCta.disabled = true;

    const item = {
      id: Number(flavour.variantId || kit.variantId),
      quantity: this.#qty,
    };
    if (this.#subscribe && kit.planId) {
      item.selling_plan = Number(kit.planId);
    }

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [item],
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();
      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id || 'starter-kit-offer', data.message, data.description, data.errors)
        );
        return;
      }
      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'starter-kit-offer', {
          source: 'starter-kit-offer',
          itemCount: 1,
          productId: String(kit.productId),
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#pending = false;
      if (buyButton) buyButton.disabled = false;
      if (stickyBuy) stickyBuy.disabled = false;
      if (cafeCta) cafeCta.disabled = false;
    }
  }

  /**
   * @param {keyof Refs} ref
   * @param {string} value
   */
  #setText(ref, value) {
    const node = this.refs[ref];
    if (node) node.textContent = value;
  }

  /**
   * @param {number} oneTime
   * @param {number} sale
   */
  #discountPercent(oneTime, sale) {
    if (oneTime <= 0 || sale >= oneTime) return 0;
    return Math.round(((oneTime - sale) / oneTime) * 100);
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [replacements]
   */
  #t(key, replacements = {}) {
    const strings = {
      subscribe_title: 'Subscribe & save',
      subscribe_copy: '{{today}} today, then {{refill}} tin refills every 4 weeks',
      onetime_title: 'One-time purchase',
      onetime_copy: 'Ships once for {{price}}, no subscription.',
      at_checkout: 'At checkout',
      perk_refills: 'Refill discount on every tin',
      perk_refills_percent: '{{percent}}% off refills',
      perk_ship: 'Free express shipping',
      perk_skip: 'Skip or cancel anytime',
      perk_guarantee: '90-day guarantee',
      cafe_line: 'Save {{amount}} vs café lattes',
      cafe_right_copy: 'for 60 lattes plus the whisk, can cup and spoon. That is {{per}} a latte, and you keep the gear.',
      cafe_keep: 'You keep {{amount}}',
      buy_now: 'Add to cart · {{price}}',
      cafe_cta: 'Get my Starter Kit · {{price}}',
      included_title: 'What you get for {{price}}',
    };
    let text = strings[key] || key;
    for (const [token, value] of Object.entries(replacements)) {
      text = text.replaceAll(`{{${token}}}`, String(value));
    }
    return text;
  }

  /**
   * @param {boolean} [fractional]
   */
  #moneyFormat(fractional = false) {
    let format = String(this.#config?.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');

    if (fractional) {
      format = format
        .replaceAll('amount_no_decimals_with_comma_separator', 'amount_with_comma_separator')
        .replaceAll('amount_no_decimals_with_space_separator', 'amount_with_space_separator')
        .replaceAll('amount_no_decimals', 'amount');
    }

    return format;
  }

  /**
   * @param {number} cents
   * @param {boolean} [fractional]
   */
  #formatMoney(cents, fractional = false) {
    return formatMoney(Math.max(cents, 0), this.#moneyFormat(fractional), this.#config?.currency || 'USD');
  }

  /**
   * @param {string} value
   */
  #escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }
}

if (!customElements.get('starter-kit-offer-component')) {
  customElements.define('starter-kit-offer-component', StarterKitOfferComponent);
}
